import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Paper, Typography, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Chip, Button,
  TextField, FormControl, InputLabel, Select, MenuItem,
  IconButton, Checkbox, Dialog, DialogTitle, DialogContent,
  DialogActions, Grid, Divider, Alert, Pagination, Card, CardContent,
  Menu, ButtonGroup
} from '@mui/material';
import {
  Print as PrintIcon,
  LocalShipping as ShippingIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  CheckCircle as CheckIcon,
  Assessment as AssessmentIcon,
  ListAlt as ListAltIcon,
  Receipt as ReceiptIcon,
  ArrowDropDown as ArrowDropDownIcon,
  LocalShippingOutlined as LogisticsIcon,
  QrCode as QrCodeIcon
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import api from '../../services/api';

const statusColors = {
  pending: 'warning',
  paid: 'info',
  shipping: 'primary',
  delivered: 'success',
  cancelled: 'error'
};

const statusLabels = {
  pending: '待付款',
  paid: '已付款',
  shipping: '出貨中',
  delivered: '已送達',
  cancelled: '已取消'
};

const paymentLabels = {
  linepay: 'LINE Pay',
  smilepay: '超商代碼'
};

const AdminOrders = () => {
  const navigate = useNavigate();
  const printRef = useRef();
  const [orders, setOrders] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState([]);
  const [filters, setFilters] = useState({
    status: '',
    payment_status: '',
    search: ''
  });
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 });
  const [printDialog, setPrintDialog] = useState(false);
  const [printLabels, setPrintLabels] = useState([]);
  const [printType, setPrintType] = useState('shipping'); // 'shipping', 'picking', 'order'
  const [printMenuAnchor, setPrintMenuAnchor] = useState(null);
  const [pickingList, setPickingList] = useState([]);
  const [orderDetails, setOrderDetails] = useState([]);
  const [logisticsLabels, setLogisticsLabels] = useState([]);
  const [creatingLogistics, setCreatingLogistics] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [detailDialog, setDetailDialog] = useState(false);

  useEffect(() => {
    fetchOrders();
    fetchStats();
  }, [filters, pagination.page]);

  const fetchOrders = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: pagination.page,
        ...filters
      });
      const response = await api.get(`/admin/orders?${params}`);
      if (response.data.success) {
        setOrders(response.data.orders);
        setPagination(response.data.pagination);
      }
    } catch (error) {
      toast.error('取得訂單失敗');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await api.get('/admin/stats');
      if (response.data.success) {
        setStats(response.data.stats);
      }
    } catch (error) {
      console.error('取得統計失敗');
    }
  };

  // 查看訂單詳情
  const handleViewDetail = async (orderNo) => {
    try {
      const response = await api.get(`/admin/orders/${orderNo}`);
      if (response.data.success) {
        setSelectedOrder(response.data.order);
        setDetailDialog(true);
      }
    } catch (error) {
      toast.error('載入訂單詳情失敗');
    }
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelected(orders.map(o => o.order_no));
    } else {
      setSelected([]);
    }
  };

  const handleSelect = (orderNo) => {
    setSelected(prev =>
      prev.includes(orderNo)
        ? prev.filter(n => n !== orderNo)
        : [...prev, orderNo]
    );
  };

  const handleBatchStatus = async (newStatus) => {
    if (selected.length === 0) {
      toast.warning('請先選擇訂單');
      return;
    }

    try {
      await api.put('/admin/orders/batch/status', {
        orderNos: selected,
        status: newStatus
      });
      toast.success(`已更新 ${selected.length} 筆訂單`);
      setSelected([]);
      fetchOrders();
      fetchStats();
    } catch (error) {
      toast.error('更新失敗');
    }
  };

  // 列印出貨單
  const handlePrintShipping = async () => {
    if (selected.length === 0) {
      toast.warning('請先選擇要列印的訂單');
      return;
    }

    try {
      const response = await api.post('/admin/orders/shipping-labels', {
        orderNos: selected
      });
      if (response.data.success) {
        setPrintLabels(response.data.labels);
        setPrintType('shipping');
        setPrintDialog(true);
      }
    } catch (error) {
      toast.error('取得出貨單失敗');
    }
  };

  // 列印撿貨單
  const handlePrintPicking = async () => {
    if (selected.length === 0) {
      toast.warning('請先選擇要列印的訂單');
      return;
    }

    try {
      const response = await api.post('/admin/orders/picking-list', {
        orderNos: selected
      });
      if (response.data.success) {
        setPickingList(response.data.pickingList);
        setPrintType('picking');
        setPrintDialog(true);
      }
    } catch (error) {
      toast.error('取得撿貨單失敗');
    }
  };

  // 列印訂單明細
  const handlePrintOrders = async () => {
    if (selected.length === 0) {
      toast.warning('請先選擇要列印的訂單');
      return;
    }

    try {
      const response = await api.post('/admin/orders/order-details', {
        orderNos: selected
      });
      if (response.data.success) {
        setOrderDetails(response.data.orders);
        setPrintType('order');
        setPrintDialog(true);
      }
    } catch (error) {
      toast.error('取得訂單明細失敗');
    }
  };

  // 舊的 handlePrint 保留相容性
  const handlePrint = handlePrintShipping;

  // 建立物流單
  const handleCreateLogistics = async () => {
    if (selected.length === 0) {
      toast.warning('請先選擇已付款的訂單');
      return;
    }

    setCreatingLogistics(true);
    try {
      const response = await api.post('/smilepay/logistics/batch-create', {
        order_nos: selected
      });
      if (response.data.success) {
        const { created, failed, results } = response.data;
        if (created > 0) {
          toast.success(`成功建立 ${created} 筆物流單`);
        }
        if (failed > 0) {
          const failedOrders = results.filter(r => !r.success);
          failedOrders.forEach(o => {
            toast.warning(`${o.orderNo}: ${o.error || '建立失敗'}`);
          });
        }
        fetchOrders();
      }
    } catch (error) {
      toast.error('建立物流單失敗: ' + (error.response?.data?.error || error.message));
    } finally {
      setCreatingLogistics(false);
    }
  };

  // 列印物流標籤
  const handlePrintLogistics = async () => {
    if (selected.length === 0) {
      toast.warning('請先選擇要列印的訂單');
      return;
    }

    try {
      const response = await api.post('/smilepay/logistics/labels', {
        order_nos: selected
      });
      if (response.data.success) {
        setLogisticsLabels(response.data.labels);
        setPrintType('logistics');
        setPrintDialog(true);
      }
    } catch (error) {
      toast.error('取得物流標籤失敗');
    }
  };

  const doPrint = () => {
    const printContent = document.getElementById('print-content');
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html>
        <head>
          <title>列印</title>
          <style>
            body { font-family: Arial, 'Microsoft JhengHei', sans-serif; margin: 0; padding: 10px; }
            table { width: 100%; border-collapse: collapse; }
            th, td { border: 1px solid #ddd; padding: 6px 8px; font-size: 13px; }
            th { background: #f5f5f5; font-weight: bold; }
            img { max-width: 50px; max-height: 50px; object-fit: cover; }
            .label { page-break-after: always; padding: 15px; border: 1px dashed #ccc; margin-bottom: 15px; }
            .label:last-child { page-break-after: auto; }
            .header { font-size: 18px; font-weight: bold; margin-bottom: 10px; border-bottom: 2px solid #000; padding-bottom: 5px; }
            .store-info { background: #f5f5f5; padding: 10px; margin: 10px 0; }
            .store-name { font-size: 16px; font-weight: bold; }
            .customer { margin: 10px 0; }
            .items { margin: 10px 0; }
            .total { font-size: 16px; font-weight: bold; text-align: right; margin-top: 10px; }
            .barcode { text-align: center; font-family: monospace; font-size: 24px; letter-spacing: 3px; margin: 15px 0; }
            .shelf { color: #d32f2f; font-weight: bold; font-size: 15px; }
            .check-box { display: inline-block; width: 20px; height: 20px; border: 2px solid #999; }
            @media print {
              .label { border: none; }
              body { padding: 0; }
            }
          </style>
        </head>
        <body>
          ${printContent.innerHTML}
        </body>
      </html>
    `);
    printWindow.document.close();
    // 等圖片載入完成再列印
    printWindow.onload = () => printWindow.print();
    setTimeout(() => { if (!printWindow.closed) printWindow.print(); }, 2000);
  };

  const formatDate = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleString('zh-TW');
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* 頂部統計 */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} sm={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography color="text.secondary" variant="body2">今日訂單</Typography>
              <Typography variant="h4" color="primary">{stats.today_orders || 0}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography color="text.secondary" variant="body2">待出貨</Typography>
              <Typography variant="h4" color="warning.main">{stats.paid_orders || 0}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography color="text.secondary" variant="body2">出貨中</Typography>
              <Typography variant="h4" color="info.main">{stats.shipping_orders || 0}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography color="text.secondary" variant="body2">總營收</Typography>
              <Typography variant="h4" color="success.main">
                ${(stats.total_revenue || 0).toLocaleString()}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* 標題與操作 */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h5" fontWeight="bold">訂單管理</Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="contained"
            color="info"
            startIcon={<AssessmentIcon />}
            onClick={() => navigate('/admin/analytics')}
          >
            數據分析
          </Button>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={() => { fetchOrders(); fetchStats(); }}
          >
            重新整理
          </Button>
          <Button
            variant="outlined"
            color="secondary"
            onClick={() => {
              localStorage.removeItem('admin_token');
              navigate('/admin');
            }}
          >
            登出
          </Button>
        </Box>
      </Box>

      {/* 篩選 */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={3}>
            <TextField
              fullWidth
              size="small"
              label="搜尋 (訂單號/姓名/電話)"
              value={filters.search}
              onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
              InputProps={{
                endAdornment: <SearchIcon color="action" />
              }}
            />
          </Grid>
          <Grid item xs={6} sm={2}>
            <FormControl fullWidth size="small">
              <InputLabel>訂單狀態</InputLabel>
              <Select
                value={filters.status}
                label="訂單狀態"
                onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
              >
                <MenuItem value="">全部</MenuItem>
                <MenuItem value="pending">待付款</MenuItem>
                <MenuItem value="paid">已付款</MenuItem>
                <MenuItem value="shipping">出貨中</MenuItem>
                <MenuItem value="delivered">已送達</MenuItem>
                <MenuItem value="cancelled">已取消</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} sm={2}>
            <FormControl fullWidth size="small">
              <InputLabel>付款狀態</InputLabel>
              <Select
                value={filters.payment_status}
                label="付款狀態"
                onChange={(e) => setFilters(prev => ({ ...prev, payment_status: e.target.value }))}
              >
                <MenuItem value="">全部</MenuItem>
                <MenuItem value="unpaid">未付款</MenuItem>
                <MenuItem value="paid">已付款</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={5}>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {/* 列印下拉選單 */}
              <ButtonGroup variant="contained" disabled={selected.length === 0}>
                <Button
                  startIcon={<PrintIcon />}
                  onClick={handlePrintShipping}
                >
                  列印 ({selected.length})
                </Button>
                <Button
                  size="small"
                  onClick={(e) => setPrintMenuAnchor(e.currentTarget)}
                >
                  <ArrowDropDownIcon />
                </Button>
              </ButtonGroup>
              <Menu
                anchorEl={printMenuAnchor}
                open={Boolean(printMenuAnchor)}
                onClose={() => setPrintMenuAnchor(null)}
              >
                <MenuItem onClick={() => { handlePrintShipping(); setPrintMenuAnchor(null); }}>
                  <ShippingIcon sx={{ mr: 1 }} /> 出貨單
                </MenuItem>
                <MenuItem onClick={() => { handlePrintPicking(); setPrintMenuAnchor(null); }}>
                  <ListAltIcon sx={{ mr: 1 }} /> 撿貨單
                </MenuItem>
                <MenuItem onClick={() => { handlePrintOrders(); setPrintMenuAnchor(null); }}>
                  <ReceiptIcon sx={{ mr: 1 }} /> 訂單明細
                </MenuItem>
                <Divider />
                <MenuItem onClick={() => { handlePrintLogistics(); setPrintMenuAnchor(null); }}>
                  <QrCodeIcon sx={{ mr: 1 }} /> 物流標籤
                </MenuItem>
              </Menu>
              <Button
                variant="contained"
                color="warning"
                startIcon={<LogisticsIcon />}
                onClick={handleCreateLogistics}
                disabled={selected.length === 0 || creatingLogistics}
              >
                {creatingLogistics ? '建立中...' : '建立物流單'}
              </Button>
              <Button
                variant="outlined"
                color="primary"
                startIcon={<ShippingIcon />}
                onClick={() => handleBatchStatus('shipping')}
                disabled={selected.length === 0}
              >
                標記出貨
              </Button>
              <Button
                variant="outlined"
                color="success"
                startIcon={<CheckIcon />}
                onClick={() => handleBatchStatus('delivered')}
                disabled={selected.length === 0}
              >
                標記送達
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* 訂單列表 */}
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: '#f5f5f5' }}>
              <TableCell padding="checkbox">
                <Checkbox
                  checked={selected.length === orders.length && orders.length > 0}
                  indeterminate={selected.length > 0 && selected.length < orders.length}
                  onChange={handleSelectAll}
                />
              </TableCell>
              <TableCell>訂單編號</TableCell>
              <TableCell>收件人</TableCell>
              <TableCell>取貨門市</TableCell>
              <TableCell>商品</TableCell>
              <TableCell align="right">金額</TableCell>
              <TableCell>付款方式</TableCell>
              <TableCell>狀態</TableCell>
              <TableCell>建立時間</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {orders.map(order => (
              <TableRow
                key={order.order_no}
                hover
                selected={selected.includes(order.order_no)}
              >
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={selected.includes(order.order_no)}
                    onChange={() => handleSelect(order.order_no)}
                  />
                </TableCell>
                <TableCell>
                  <Typography
                    variant="body2"
                    fontWeight="bold"
                    color="primary"
                    sx={{ cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
                    onClick={() => handleViewDetail(order.order_no)}
                  >
                    {order.order_no}
                  </Typography>
                  {order.logistics_status === 'failed' && (
                    <Chip
                      size="small"
                      color="error"
                      label="物流建單失敗"
                      title={order.logistics_error || ''}
                      sx={{ mt: 0.5, height: 20, fontSize: '0.7rem' }}
                    />
                  )}
                </TableCell>
                <TableCell>
                  <Typography variant="body2">{order.customer_name}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {order.customer_phone}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">{order.store_type}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {order.store_name}
                  </Typography>
                </TableCell>
                <TableCell sx={{ maxWidth: 200 }}>
                  <Typography variant="caption" noWrap title={order.products}>
                    {order.products?.substring(0, 40)}...
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography fontWeight="bold" color="error">
                    ${order.total_amount}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    label={paymentLabels[order.payment_method] || order.payment_method}
                    color={order.payment_method === 'linepay' ? 'success' : 'default'}
                  />
                </TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    label={statusLabels[order.status]}
                    color={statusColors[order.status]}
                  />
                </TableCell>
                <TableCell>
                  <Typography variant="caption">
                    {formatDate(order.created_at)}
                  </Typography>
                </TableCell>
              </TableRow>
            ))}
            {orders.length === 0 && (
              <TableRow>
                <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">
                    {loading ? '載入中...' : '沒有訂單資料'}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* 分頁 */}
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
        <Pagination
          count={pagination.pages}
          page={pagination.page}
          onChange={(e, page) => setPagination(prev => ({ ...prev, page }))}
          color="primary"
        />
      </Box>

      {/* 列印對話框 */}
      <Dialog open={printDialog} onClose={() => setPrintDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {printType === 'shipping' && `出貨單預覽 (${printLabels.length} 張)`}
          {printType === 'picking' && `撿貨單預覽 (${pickingList.length} 項)`}
          {printType === 'order' && `訂單明細預覽 (${orderDetails.length} 筆)`}
          {printType === 'logistics' && `物流標籤預覽 (${logisticsLabels.length} 張)`}
        </DialogTitle>
        <DialogContent>
          <div id="print-content">
            {/* 出貨單列印 */}
            {printType === 'shipping' && printLabels.map((label, index) => (
              <div key={label.orderNo} className="label" style={{
                border: '2px solid #000',
                padding: 0,
                marginBottom: 20,
                pageBreakAfter: index < printLabels.length - 1 ? 'always' : 'auto',
                maxWidth: 420,
                margin: '0 auto 20px'
              }}>
                {/* 標頭：物流公司 + 配送方式 */}
                <div style={{ background: '#000', color: '#fff', padding: '8px 12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="subtitle1" fontWeight="bold" color="inherit">新竹物流</Typography>
                  <Typography variant="body2" color="inherit">
                    {label.storeType === '宅配到府' ? '宅配' : '超商取貨'} | 常溫
                  </Typography>
                </div>

                {/* 代收金額區 */}
                {label.paymentMethod === 'cod' && (
                  <div style={{ background: '#fff3e0', padding: '8px 12px', borderBottom: '2px solid #000', textAlign: 'center' }}>
                    <Typography variant="caption" display="block">代收貨款</Typography>
                    <Typography variant="h4" fontWeight="bold" color="error">
                      ${Number(label.totalAmount).toLocaleString()}
                    </Typography>
                  </div>
                )}

                {/* 訂單編號 */}
                <div style={{ padding: '6px 12px', borderBottom: '1px solid #ccc', background: '#f9f9f9', textAlign: 'center' }}>
                  <Typography variant="caption" display="block" color="text.secondary">訂單編號</Typography>
                  <Typography variant="body1" fontFamily="monospace" fontWeight="bold" letterSpacing={2}>
                    {label.orderNo}
                  </Typography>
                </div>

                {/* 寄件人 */}
                <div style={{ padding: '8px 12px', borderBottom: '1px solid #ddd' }}>
                  <Typography variant="caption" color="text.secondary" display="block">寄件人</Typography>
                  <Grid container>
                    <Grid item xs={6}>
                      <Typography variant="body2" fontWeight="bold">優爾商城</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2">04-7380686</Typography>
                    </Grid>
                  </Grid>
                </div>

                {/* 收件人 */}
                <div style={{ padding: '10px 12px', borderBottom: '2px solid #000', background: '#f5f5f5' }}>
                  <Typography variant="caption" color="text.secondary" display="block">收件人</Typography>
                  <Grid container>
                    <Grid item xs={6}>
                      <Typography variant="h6" fontWeight="bold">{label.customerName}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body1" fontWeight="bold">{label.customerPhone}</Typography>
                    </Grid>
                  </Grid>
                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                    {label.storeType === '宅配到府'
                      ? label.storeAddress
                      : `${label.storeType} ${label.storeName} (${label.storeId})`
                    }
                  </Typography>
                  {label.storeType !== '宅配到府' && label.storeAddress && (
                    <Typography variant="caption" color="text.secondary">{label.storeAddress}</Typography>
                  )}
                </div>

                {/* 商品明細 */}
                <div style={{ padding: '8px 12px', borderBottom: '1px solid #ddd' }}>
                  <Typography variant="caption" color="text.secondary" display="block">品名/內容物</Typography>
                  <Typography variant="body2" style={{ whiteSpace: 'pre-line' }}>
                    {label.items}
                  </Typography>
                </div>

                {/* 金額明細 + 備註 */}
                <div style={{ padding: '8px 12px', display: 'flex', justifyContent: 'space-between' }}>
                  <div>
                    <Typography variant="caption" color="text.secondary">
                      商品 ${label.subtotal} + 運費 ${label.shippingFee || 0}
                    </Typography>
                    {label.remark && (
                      <Typography variant="caption" display="block" color="warning.main">
                        備註: {label.remark}
                      </Typography>
                    )}
                  </div>
                  <Typography variant="body2" color="text.secondary">
                    {new Date(label.createdAt).toLocaleDateString('zh-TW')}
                  </Typography>
                </div>
              </div>
            ))}

            {/* 撿貨單列印 */}
            {printType === 'picking' && (
              <div style={{ padding: 20 }}>
                <div style={{ borderBottom: '2px solid #000', paddingBottom: 10, marginBottom: 20 }}>
                  <Typography variant="h5" fontWeight="bold">撿貨單</Typography>
                  <Typography variant="body2" color="text.secondary">
                    列印時間: {new Date().toLocaleString('zh-TW')} | 訂單數量: {selected.length} 筆 | 商品項數: {pickingList.length} 項 | 總件數: {pickingList.reduce((sum, item) => sum + item.totalQuantity, 0)} 件
                  </Typography>
                </div>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                      <TableCell width={40} align="center">#</TableCell>
                      <TableCell width={60}>圖片</TableCell>
                      <TableCell width={80}>貨架</TableCell>
                      <TableCell width={100}>貨號</TableCell>
                      <TableCell>商品名稱</TableCell>
                      <TableCell width={70} align="center">數量</TableCell>
                      <TableCell width={50} align="center">✓</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {pickingList.map((item, index) => (
                      <TableRow key={index} sx={{ '&:nth-of-type(odd)': { bgcolor: '#fafafa' } }}>
                        <TableCell align="center">{index + 1}</TableCell>
                        <TableCell>
                          {item.imageUrl ? (
                            <img
                              src={item.imageUrl.startsWith('http') ? item.imageUrl : `https://store.urstore.tw${item.imageUrl}`}
                              alt=""
                              style={{ width: 50, height: 50, objectFit: 'cover', borderRadius: 4 }}
                              onError={(e) => { e.target.style.display = 'none'; }}
                            />
                          ) : '-'}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight="bold" color="error.main">
                            {item.shelfLocation}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontFamily="monospace">
                            {item.sku}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{item.productName}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {item.orderNos}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Typography variant="h6" fontWeight="bold" color="primary">
                            {item.totalQuantity}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Box sx={{ width: 24, height: 24, border: '2px solid #999', borderRadius: 1 }} />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}

            {/* 訂單明細列印 */}
            {printType === 'order' && orderDetails.map((order, index) => (
              <div key={order.orderNo} style={{
                border: '1px solid #ccc',
                padding: 20,
                marginBottom: 20,
                pageBreakAfter: index < orderDetails.length - 1 ? 'always' : 'auto'
              }}>
                <div style={{ borderBottom: '2px solid #000', paddingBottom: 10, marginBottom: 15 }}>
                  <Typography variant="h6" fontWeight="bold">訂單明細</Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Typography variant="body2">訂單編號: {order.orderNo}</Typography>
                      <Typography variant="body2">訂單日期: {new Date(order.createdAt).toLocaleString('zh-TW')}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2">付款方式: {order.paymentMethod}</Typography>
                      <Typography variant="body2">付款狀態: {order.paymentStatus}</Typography>
                    </Grid>
                  </Grid>
                </div>

                <Grid container spacing={2} sx={{ mb: 2 }}>
                  <Grid item xs={6}>
                    <Typography variant="subtitle2" fontWeight="bold">收件人資訊</Typography>
                    <Typography variant="body2">姓名: {order.customerName}</Typography>
                    <Typography variant="body2">電話: {order.customerPhone}</Typography>
                    <Typography variant="body2">Email: {order.customerEmail || '-'}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="subtitle2" fontWeight="bold">取貨門市</Typography>
                    <Typography variant="body2">{order.storeType} - {order.storeName}</Typography>
                    <Typography variant="body2">{order.storeAddress}</Typography>
                    <Typography variant="body2">門市代號: {order.storeId}</Typography>
                  </Grid>
                </Grid>

                <Divider sx={{ my: 2 }} />

                <Typography variant="subtitle2" fontWeight="bold" gutterBottom>商品明細</Typography>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                      <TableCell>商品名稱</TableCell>
                      <TableCell width={100}>SKU</TableCell>
                      <TableCell width={80} align="center">數量</TableCell>
                      <TableCell width={100} align="right">單價</TableCell>
                      <TableCell width={100} align="right">小計</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {order.items.map((item, idx) => (
                      <TableRow key={idx}>
                        <TableCell>{item.productName}</TableCell>
                        <TableCell>{item.sku}</TableCell>
                        <TableCell align="center">{item.quantity}</TableCell>
                        <TableCell align="right">${item.price}</TableCell>
                        <TableCell align="right">${item.price * item.quantity}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>

                <Box sx={{ mt: 2, textAlign: 'right' }}>
                  <Typography variant="body2">商品小計: ${order.subtotal}</Typography>
                  <Typography variant="body2">運費: ${order.shippingFee || 0}</Typography>
                  <Typography variant="h6" fontWeight="bold" color="error">
                    總計: ${order.totalAmount}
                  </Typography>
                </Box>

                {order.remark && (
                  <Alert severity="info" sx={{ mt: 2 }}>
                    訂單備註: {order.remark}
                  </Alert>
                )}
              </div>
            ))}

            {/* 物流標籤列印 */}
            {printType === 'logistics' && logisticsLabels.map((label, index) => (
              <div key={label.orderNo} className="label" style={{
                border: '2px solid #000',
                padding: 20,
                marginBottom: 20,
                pageBreakAfter: index < logisticsLabels.length - 1 ? 'always' : 'auto'
              }}>
                <div style={{ textAlign: 'center', borderBottom: '2px solid #000', paddingBottom: 10, marginBottom: 15 }}>
                  <Typography variant="h5" fontWeight="bold">{label.storeTypeName}</Typography>
                  <Typography variant="body2">超商取貨</Typography>
                </div>

                {/* 物流單號區 */}
                <Box sx={{
                  background: '#f0f0f0',
                  p: 2,
                  textAlign: 'center',
                  mb: 2,
                  border: '1px solid #ccc'
                }}>
                  {label.smilePayNo ? (
                    <>
                      <Typography variant="caption" display="block">物流單號</Typography>
                      <Typography variant="h4" fontFamily="monospace" fontWeight="bold">
                        {label.smilePayNo}
                      </Typography>
                      {label.trackingNo && (
                        <Typography variant="body2" color="text.secondary">
                          追蹤號: {label.trackingNo}
                        </Typography>
                      )}
                    </>
                  ) : (
                    <Alert severity="warning" sx={{ mb: 0 }}>
                      尚未建立物流單
                    </Alert>
                  )}
                </Box>

                {/* 門市資訊 */}
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" fontWeight="bold">取貨門市</Typography>
                  <Typography variant="h6">{label.storeName}</Typography>
                  <Typography variant="body2">{label.storeAddress}</Typography>
                  <Typography variant="caption">門市代號: {label.storeId}</Typography>
                </Box>

                {/* 收件人資訊 */}
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" fontWeight="bold">收件人</Typography>
                  <Typography variant="body1">{label.customerName}</Typography>
                  <Typography variant="body2">{label.customerPhone}</Typography>
                </Box>

                <Divider sx={{ my: 2 }} />

                {/* 訂單資訊 */}
                <Grid container spacing={1}>
                  <Grid item xs={6}>
                    <Typography variant="caption">訂單編號</Typography>
                    <Typography variant="body2" fontWeight="bold">{label.orderNo}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption">訂單金額</Typography>
                    <Typography variant="body2" fontWeight="bold" color="error">
                      ${label.totalAmount}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption">物流狀態</Typography>
                    <Typography variant="body2">{label.logisticsStatusText}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption">列印時間</Typography>
                    <Typography variant="body2">
                      {new Date().toLocaleString('zh-TW')}
                    </Typography>
                  </Grid>
                </Grid>

                {/* 商品清單 */}
                <Box sx={{ mt: 2, p: 1, bgcolor: '#f9f9f9' }}>
                  <Typography variant="caption" fontWeight="bold">商品內容</Typography>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
                    {label.items}
                  </Typography>
                </Box>

                {/* 條碼區 (如果有物流單號) */}
                {label.smilePayNo && (
                  <Box sx={{
                    textAlign: 'center',
                    mt: 2,
                    p: 1,
                    border: '1px solid #000',
                    fontFamily: 'monospace',
                    fontSize: 24,
                    letterSpacing: 3
                  }}>
                    {label.smilePayNo}
                  </Box>
                )}
              </div>
            ))}
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPrintDialog(false)}>關閉</Button>
          <Button variant="contained" startIcon={<PrintIcon />} onClick={doPrint}>
            列印
          </Button>
        </DialogActions>
      </Dialog>

      {/* 訂單詳情對話框 */}
      <Dialog open={detailDialog} onClose={() => setDetailDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          訂單詳情 - {selectedOrder?.order_no}
        </DialogTitle>
        <DialogContent dividers>
          {selectedOrder && (
            <Grid container spacing={3}>
              {/* 訂單基本資訊 */}
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
                  <Chip
                    label={statusLabels[selectedOrder.status] || selectedOrder.status}
                    color={statusColors[selectedOrder.status] || 'default'}
                  />
                  <Chip
                    label={selectedOrder.payment_status === 'paid' ? '已付款' : '未付款'}
                    color={selectedOrder.payment_status === 'paid' ? 'success' : 'warning'}
                    variant="outlined"
                  />
                  <Typography variant="body2" color="text.secondary">
                    建立時間: {new Date(selectedOrder.created_at).toLocaleString('zh-TW')}
                  </Typography>
                </Box>
              </Grid>

              {/* 收件人資訊 */}
              <Grid item xs={12} md={6}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                      📦 收件人資訊
                    </Typography>
                    <Typography variant="body2">姓名: {selectedOrder.customer_name}</Typography>
                    <Typography variant="body2">電話: {selectedOrder.customer_phone}</Typography>
                    <Typography variant="body2">Email: {selectedOrder.customer_email || '-'}</Typography>
                  </CardContent>
                </Card>
              </Grid>

              {/* 付款資訊 */}
              <Grid item xs={12} md={6}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                      💳 付款資訊
                    </Typography>
                    <Typography variant="body2">
                      付款方式: {paymentLabels[selectedOrder.payment_method] || selectedOrder.payment_method || '超商取貨付款'}
                    </Typography>
                    <Typography variant="body2">
                      付款狀態: {selectedOrder.payment_status === 'paid' ? '已付款' : '未付款'}
                    </Typography>
                    {selectedOrder.paid_at && (
                      <Typography variant="body2">
                        付款時間: {new Date(selectedOrder.paid_at).toLocaleString('zh-TW')}
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              </Grid>

              {/* 取貨門市資訊 */}
              <Grid item xs={12}>
                <Card variant="outlined" sx={{ bgcolor: '#f5f5f5' }}>
                  <CardContent>
                    <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                      🏪 取貨門市
                    </Typography>
                    <Typography variant="body2" fontWeight="bold">
                      {selectedOrder.store_type === '7-ELEVEN' ? '7-ELEVEN' : selectedOrder.store_type} - {selectedOrder.store_name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      地址: {selectedOrder.store_address}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      門市代號: {selectedOrder.store_id}
                    </Typography>
                    {selectedOrder.smilepay_no && (
                      <Alert severity="info" sx={{ mt: 1 }}>
                        物流單號: {selectedOrder.smilepay_no}
                      </Alert>
                    )}
                  </CardContent>
                </Card>
              </Grid>

              {/* 商品明細 */}
              <Grid item xs={12}>
                <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                  🛒 商品明細
                </Typography>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                      <TableCell>商品名稱</TableCell>
                      <TableCell width={100}>SKU</TableCell>
                      <TableCell width={80} align="center">數量</TableCell>
                      <TableCell width={100} align="right">單價</TableCell>
                      <TableCell width={100} align="right">小計</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {selectedOrder.items?.map((item, idx) => (
                      <TableRow key={idx}>
                        <TableCell>{item.product_name}</TableCell>
                        <TableCell>{item.product_sku}</TableCell>
                        <TableCell align="center">{item.quantity}</TableCell>
                        <TableCell align="right">${item.price}</TableCell>
                        <TableCell align="right">${item.subtotal}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Grid>

              {/* 金額摘要 */}
              <Grid item xs={12}>
                <Box sx={{ textAlign: 'right', mt: 2 }}>
                  <Typography variant="body2">商品小計: ${selectedOrder.subtotal}</Typography>
                  <Typography variant="body2">運費: ${selectedOrder.shipping_fee || 60}</Typography>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="h6" fontWeight="bold" color="error">
                    總計: ${selectedOrder.total_amount}
                  </Typography>
                </Box>
              </Grid>

              {/* 訂單備註 */}
              {selectedOrder.remark && (
                <Grid item xs={12}>
                  <Alert severity="info">
                    訂單備註: {selectedOrder.remark}
                  </Alert>
                </Grid>
              )}
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailDialog(false)}>關閉</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminOrders;
